'use client'

import type { createTRPCContext } from '@trpc/tanstack-react-query'
import { createContext, useContext } from 'react'

import type { AppRouter } from '@package/api'

/**
 * Type for the return value of useTRPC() from @trpc/tanstack-react-query
 * This provides full type safety for all router procedures
 */
export type SharedTRPCContext = ReturnType<ReturnType<typeof createTRPCContext<AppRouter>>['useTRPC']>

const TRPCContext = createContext<SharedTRPCContext | null>(null)

export interface SharedTRPCProviderProps {
  /** The return value of your app's useTRPC() hook */
  trpc: SharedTRPCContext
  children: React.ReactNode
}

/**
 * Provider that makes tRPC hooks available to shared components.
 *
 * @example
 * ```tsx
 * // In your app's providers (e.g., apps/admin/src/app/providers.tsx)
 * 'use client'
 * import { SharedTRPCProvider } from '@ui/shared/trpc'
 * import { useTRPC } from '~/trpc/react'
 *
 * function InnerProviders({ children }) {
 *   const trpc = useTRPC()
 *   return (
 *     <SharedTRPCProvider trpc={trpc}>
 *       {children}
 *     </SharedTRPCProvider>
 *   )
 * }
 *
 * // Wrap InnerProviders inside your TRPCReactProvider
 * export function Providers({ children }) {
 *   return (
 *     <TRPCReactProvider>
 *       <InnerProviders>{children}</InnerProviders>
 *     </TRPCReactProvider>
 *   )
 * }
 * ```
 */
export function SharedTRPCProvider({ trpc, children }: SharedTRPCProviderProps) {
  return <TRPCContext.Provider value={trpc}>{children}</TRPCContext.Provider>
}

/**
 * Hook to access tRPC from shared components.
 * Must be used within a SharedTRPCProvider.
 *
 * @example
 * ```tsx
 * // In a shared component
 * const trpc = useSharedTRPC()
 * const { data } = trpc.invoice.getPdf.useQuery({ invoiceId })
 * ```
 */
export function useSharedTRPC(): SharedTRPCContext {
  const trpc = useContext(TRPCContext)
  if (!trpc) {
    throw new Error(
      'useSharedTRPC must be used within a SharedTRPCProvider. Wrap your app with <SharedTRPCProvider trpc={useTRPC()}>.',
    )
  }
  return trpc
}
